import { Building, Floor, Apartment, ApartmentStatus } from '../types/building';
import * as pdfjsLib from 'pdfjs-dist';

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Status mapping for import
const statusMapping: Record<string, ApartmentStatus> = {
  '1': 'completed',
  '1-K': 'completed_with_cable',
  '10': 'completed_no_measurement',
  '2': 'tenant_absent',
  '3': 'access_denied',
  '4': 'tenant_affected_no_access',
  '4-K': 'tenant_affected_with_cable',
  '5': 'present_no_access',
  '6': 'appointment_cancelled',
  'Abbruch': 'aborted',
  'Ausstehend': 'pending',
  'In Bearbeitung': 'in_progress'
};

// Extract building data from PDF
export const extractBuildingFromPDF = async (file: File): Promise<Building> => {
  if (!file) {
    throw new Error('Keine Datei ausgewählt');
  }

  if (file.type !== 'application/pdf') {
    throw new Error('Die ausgewählte Datei ist keine PDF-Datei');
  }

  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    
    fileReader.onload = async (event) => {
      if (!event.target?.result) {
        reject(new Error('Fehler beim Lesen der Datei'));
        return;
      }
      
      try {
        const arrayBuffer = event.target.result as ArrayBuffer;
        const pdfData = new Uint8Array(arrayBuffer);
        
        // First try to extract metadata if this is an InstallTrack Pro PDF
        try {
          const loadingTask = pdfjsLib.getDocument({ data: pdfData });
          const pdf = await loadingTask.promise;
          const page1 = await pdf.getPage(1);
          const content1 = await page1.getTextContent();
          const text1 = content1.items.map((item: any) => item.str).join(' ');
          
          // Look for metadata
          const metadataMatch = text1.match(/<!--INSTALLTRACK_METADATA:(.+?)-->/);
          if (metadataMatch) {
            try {
              const metadata = JSON.parse(metadataMatch[1]);
              if (metadata.type === 'InstallTrack Pro Export' && metadata.building) {
                // Convert dates back to Date objects
                const building = {
                  ...metadata.building,
                  floors: metadata.building.floors.map((floor: any) => ({
                    ...floor,
                    apartments: floor.apartments.map((apt: any) => ({
                      ...apt,
                      lastUpdated: apt.lastUpdated ? new Date(apt.lastUpdated) : undefined
                    }))
                  }))
                };
                
                resolve(building);
                return;
              }
            } catch (metadataError) {
              console.warn('Failed to parse metadata, falling back to visual parsing:', metadataError);
            }
          }
        } catch (metadataError) {
          console.warn('Failed to check for metadata, falling back to visual parsing:', metadataError);
        }
        
        // If no metadata found or parsing failed, try visual parsing
        const loadingTask = pdfjsLib.getDocument({ data: pdfData });
        const pdf = await loadingTask.promise;
        
        if (pdf.numPages === 0) {
          throw new Error('Die PDF-Datei enthält keine Seiten');
        }
        
        // Extract building info from first page
        const page1 = await pdf.getPage(1);
        const content1 = await page1.getTextContent();
        const text1 = content1.items.map((item: any) => item.str).join(' ');
        
        // Try different patterns to extract building info
        let buildingName: string;
        let address: string;
        
        // Pattern 1: Standard format
        const standardNameMatch = text1.match(/InstallTrack Pro\s+(.+?)\s+Dokumentation erstellt/);
        const standardAddressMatch = text1.match(/erstellt\s+(.+?)\s+Stand:/);
        
        // Pattern 2: Simple format
        const simpleNameMatch = text1.match(/InstallTrack Pro\s+(.+?)$/m);
        const addressLine = text1.split('\n').find(line => 
          line.includes('straße') || line.includes('weg') || line.includes('allee') || 
          /\d{5}/.test(line) // Postal code pattern
        );
        
        // Pattern 3: Fallback format
        const fallbackNameMatch = text1.match(/Gebäude:\s+(.+?)[\n\r]/);
        const fallbackAddressMatch = text1.match(/Adresse:\s+(.+?)[\n\r]/);
        
        if (standardNameMatch && standardAddressMatch) {
          buildingName = standardNameMatch[1].trim();
          address = standardAddressMatch[1].trim();
        } else if (simpleNameMatch && addressLine) {
          buildingName = simpleNameMatch[1].trim();
          address = addressLine.trim();
        } else if (fallbackNameMatch && fallbackAddressMatch) {
          buildingName = fallbackNameMatch[1].trim();
          address = fallbackAddressMatch[1].trim();
        } else {
          throw new Error('Konnte keine Gebäudeinformationen aus der PDF extrahieren. Bitte stellen Sie sicher, dass die PDF mit InstallTrack Pro erstellt wurde.');
        }
        
        // Create building structure
        const building: Building = {
          id: crypto.randomUUID(),
          organizationId: 'default-org',
          name: buildingName,
          address: address,
          floors: [],
          metadata: {
            status: 'planning',
            startDate: new Date()
          }
        };
        
        // Process remaining pages for floor and apartment data
        for (let i = 2; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          const text = content.items.map((item: any) => item.str).join(' ');
          
          // Extract floor information
          const floorMatches = text.match(/([0-9]+\. OG|EG|[0-9]+\. UG)/g);
          
          if (floorMatches) {
            for (const floorMatch of floorMatches) {
              let floorNumber: number;
              
              if (floorMatch === 'EG') {
                floorNumber = 0;
              } else if (floorMatch.includes('UG')) {
                floorNumber = -parseInt(floorMatch.match(/([0-9]+)/)?.[1] || '1', 10);
              } else {
                floorNumber = parseInt(floorMatch.match(/([0-9]+)/)?.[1] || '1', 10);
              }
              
              // Check if floor already exists
              let floor = building.floors.find(f => f.number === floorNumber);
              
              if (!floor) {
                floor = {
                  id: crypto.randomUUID(),
                  number: floorNumber,
                  apartments: []
                };
                building.floors.push(floor);
              }
              
              // Extract apartment information
              const apartmentMatches = text.match(/(WE|Kellerbereich)\s+([0-9-]+)/g);
              
              if (apartmentMatches) {
                for (const aptMatch of apartmentMatches) {
                  const areaType = aptMatch.startsWith('WE') ? 'residential' : 'cellar';
                  const formattedNumber = aptMatch.match(/([0-9-]+)/)?.[1];
                  
                  if (!formattedNumber) continue;
                  
                  // Extract status and notes
                  const statusRegex = new RegExp(`${formattedNumber}.*?Status:\\s+([^\\n]+)`, 's');
                  const statusMatch = text.match(statusRegex);
                  
                  const notesRegex = new RegExp(`${formattedNumber}.*?Notizen:\\s+([^\\n]+)`, 's');
                  const notesMatch = text.match(notesRegex);
                  
                  // Extract technician info
                  const technicianRegex = new RegExp(`${formattedNumber}.*?Techniker:\\s+([^\\n]+)`, 's');
                  const technicianMatch = text.match(technicianRegex);
                  
                  let status: ApartmentStatus = 'pending';
                  let notes = '';
                  let abortReason: string | undefined;
                  let technician: string | undefined;
                  
                  if (statusMatch) {
                    const statusText = statusMatch[1].trim();
                    
                    // Check if it's an abort with reason
                    if (statusText.startsWith('Abbruch:')) {
                      status = 'aborted';
                      abortReason = statusText.replace('Abbruch:', '').trim();
                    } else {
                      // Map the status text to ApartmentStatus
                      for (const [key, value] of Object.entries(statusMapping)) {
                        if (statusText.includes(key)) {
                          status = value;
                          break;
                        }
                      }
                    }
                  }
                  
                  if (notesMatch) {
                    notes = notesMatch[1].trim();
                  }
                  
                  if (technicianMatch) {
                    technician = technicianMatch[1].trim();
                  }
                  
                  // Parse apartment number from formatted number
                  const numberMatch = formattedNumber.match(/[0-9-]+-([0-9]+)/);
                  const number = numberMatch ? parseInt(numberMatch[1], 10) : 1;
                  
                  // Create apartment
                  const apartment: Apartment = {
                    id: crypto.randomUUID(),
                    number,
                    formattedNumber,
                    status,
                    notes,
                    areaType,
                    lastUpdated: new Date(),
                    technician
                  };
                  
                  if (abortReason) {
                    apartment.abortReason = abortReason;
                  }
                  
                  floor.apartments.push(apartment);
                }
              }
            }
          }
        }
        
        // Validate the extracted data
        if (building.floors.length === 0) {
          throw new Error('Keine Etagen in der PDF gefunden. Bitte überprüfen Sie das Format der PDF-Datei.');
        }
        
        resolve(building);
      } catch (error) {
        console.error('Error parsing PDF:', error);
        reject(new Error(error instanceof Error ? error.message : 'Fehler beim Parsen der PDF-Datei. Bitte stellen Sie sicher, dass die Datei nicht beschädigt ist.'));
      }
    };
    
    fileReader.onerror = () => {
      reject(new Error('Fehler beim Lesen der Datei'));
    };
    
    fileReader.readAsArrayBuffer(file);
  });
};